import { createTheme, type Theme as MuiTheme } from "@mui/material/styles";
import { muiThemeOptions } from "@tactical-agent-neural-knowledge/sdk/blocks-web";
import type { Theme } from "@tactical-agent-neural-knowledge/sdk/contracts/workspace";
import { MiniStore, useMiniStore } from "./miniStore";
import { theme as brandTheme, themeOverrides } from "./theme";

/**
 * The theme in force: a person's own, else the workspace's, else TANK's. Set by the shell once
 * preferences and the workspace are known; read by the root, which builds the MUI theme from it.
 */
export const activeTheme = new MiniStore<{ theme: Theme | undefined }>({ theme: undefined });

export function useActiveTheme(): Theme | undefined {
  return useMiniStore(activeTheme, (s) => s.theme);
}

/** The scheme a generated theme wants: it comes with the colors. */
export function schemeOf(t: Theme | undefined): "dark" | "light" | undefined {
  if (!t) return undefined;
  return t.scheme === "light" ? "light" : "dark";
}

/** MUI palette overrides for a generated theme; applied to the scheme it names. */
export function paletteFor(t: Theme) {
  return {
    primary: { main: t.primary },
    secondary: { main: t.secondary },
    info: { main: t.accent },
    background: { default: t.background, paper: t.surface },
    text: { primary: t.onSurface },
  };
}

const cache = new Map<string, MuiTheme>();

/** The MUI theme for the theme in force. TANK's own when there is none. */
export function muiThemeFor(t: Theme | undefined): MuiTheme {
  if (!t) return brandTheme;
  const key = JSON.stringify([
    t.scheme,
    t.primary,
    t.secondary,
    t.background,
    t.surface,
    t.onSurface,
    t.accent,
  ]);
  const hit = cache.get(key);
  if (hit) return hit;
  const scheme = schemeOf(t) ?? "dark";
  const built = createTheme(
    muiThemeOptions,
    { colorSchemes: { [scheme]: { palette: paletteFor(t) } } },
    themeOverrides,
  );
  cache.set(key, built);
  return built;
}
